import { Column } from "@mdotm/shadcn/legacy/components";
import type React from "react";
import { COMPOSITION_GROUP_BY_UNGROUP, CompositionFirstLevel, type CompositionGroupBy } from "../../../state";
import { Select, type SelectOption, FormField } from "@mdotm/shadcn/mdotm/components";

/** First-level grouping dimension — the "Allocation" / "Holdings" select. */
export const FIRST_LEVEL_OPTIONS: SelectOption<CompositionFirstLevel>[] = [
	{ label: "Allocation", value: CompositionFirstLevel.allocation },
	{ label: "Holdings", value: CompositionFirstLevel.holdings },
];

type GroupSelectsProps = {
	groupBy: CompositionGroupBy;
	setGroupBy: (v: CompositionGroupBy) => void;
	groupByOptions: SelectOption<string>[];
};

function firstLevelSelect({ groupBy, setGroupBy }: GroupSelectsProps): React.JSX.Element {
	return (
		<Select
			value={groupBy.firstLevel}
			onChange={(v) => setGroupBy({ ...groupBy, firstLevel: v as CompositionFirstLevel })}
			options={FIRST_LEVEL_OPTIONS}
			size="x-small"
			className="shrink-0"
		/>
	);
}

function secondLevelSelect({ groupBy, setGroupBy, groupByOptions }: GroupSelectsProps): React.JSX.Element {
	return (
		<Select
			value={groupBy.secondLevel}
			onChange={(v) => setGroupBy({ ...groupBy, secondLevel: v ?? COMPOSITION_GROUP_BY_UNGROUP })}
			options={groupByOptions}
			enableSearch={groupByOptions.length > 5}
			size="x-small"
			className="shrink-0"
		/>
	);
}

/**
 * Inline grouping selects for the toolbar row. Dumb presentational stage — `labelled` just shows the
 * "Group by" label before the first select. Same selects + same `compositionGroupBy$` atom as the
 * stacked variant, so the selection persists across collapse/expand.
 */
export function GroupSelects(props: GroupSelectsProps & { labelled?: boolean }): React.JSX.Element {
	return (
		<>
			{props.labelled ? (
				<FormField label="Group by" className="flex-row gap-2 items-center mr-2 shrink-0">
					{() => firstLevelSelect(props)}
				</FormField>
			) : (
				<div className="mr-2 shrink-0">{firstLevelSelect(props)}</div>
			)}
			{secondLevelSelect(props)}
		</>
	);
}

/** Stacked, fully-labelled grouping selects for the collapsed popover stage. */
export function GroupSelectsStacked(props: GroupSelectsProps): React.JSX.Element {
	return (
		<Column gap={12} className="min-w-56">
			<FormField label="Group by">{() => firstLevelSelect(props)}</FormField>
			<FormField label="Sub-group">{() => secondLevelSelect(props)}</FormField>
		</Column>
	);
}
